import React, { useState, useEffect } from 'react';
import FestivalAmbient from '../../components/festival/FestivalAmbient';
import useInactivityLogout from '../../hooks/useInactivityLogout';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import api from '../../utils/api';
import {
  LayoutDashboard, FolderOpen, Users, Building2, ClipboardList,
  BarChart3, LogOut, Menu, GitBranch, Clock,
  Calendar, Package, MessageSquare, Settings, UserCog, ScanBarcode,
  ShoppingCart, FileText, Shield, Inbox, Headphones, ShieldCheck, Truck, CheckSquare, FileSpreadsheet, Paintbrush, Wallet, UsersRound, ClipboardCheck, Ruler, Globe, FileSignature, ScrollText /* , Sparkles */} from 'lucide-react';
import NotificationBell from '../../components/NotificationBell';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  // { to: '/admin/ask-moji', label: 'Ask Moji', icon: Sparkles },   // Ask Moji — hidden for now
  { to: '/admin/projects', label: 'Projects', icon: FolderOpen },
  { to: '/admin/main-po', label: 'Manufacturing Order', icon: FileSpreadsheet },
  { to: '/admin/finish', label: 'Finish Sheets', icon: Paintbrush },
  { to: '/admin/quotations', label: 'Quotations', icon: FileSignature },
  { to: '/admin/cash-book', label: 'Cash Book', icon: Wallet },
  { to: '/admin/payroll', label: 'Payroll', icon: UsersRound },
  { to: '/admin/quality', label: 'Quality Reports', icon: ClipboardCheck },
  { to: '/admin/cft', label: 'CFT Sheets', icon: Ruler },
  { to: '/admin/outsource', label: 'Outsource', icon: Truck },
  { to: '/admin/tasks', label: 'Tasks', icon: ClipboardList },
  { to: '/admin/workers', label: 'Workers', icon: Users },
  { to: '/admin/user-rights', label: 'User Rights', icon: ShieldCheck },
  { to: '/admin/approvals', label: 'Approvals', icon: CheckSquare },
  { to: '/admin/clients', label: 'Clients', icon: UserCog },
  { to: '/admin/departments', label: 'Departments', icon: Building2 },
  { to: '/admin/reports', label: 'Reports', icon: BarChart3 },
  { to: '/admin/product-tracking', label: 'Stage Tracker', icon: GitBranch },
  { to: '/admin/time-report', label: 'Time Report', icon: Clock },
  { to: '/admin/daily-progress', label: 'Daily Progress', icon: Calendar },
  { to: '/admin/packing', label: 'Packing', icon: Package },
  { to: '/admin/barcodes', label: 'Barcodes', icon: ScanBarcode },
  // { to: '/admin/purchase-orders', label: 'Purchase Orders', icon: ShoppingCart }, // commented out
  { to: '/admin/sales', label: 'Sales', icon: ShoppingCart },
  { to: '/admin/sale-challans', label: 'Sale Challans', icon: FileText },
  { to: '/admin/client-pos', label: 'Client POs', icon: Inbox },
  { to: '/admin/client-po-report', label: 'PO Report', icon: FileText },
  { to: '/admin/client-tickets', label: 'Client Support', icon: Headphones },
  { to: '/admin/queries', label: 'Queries', icon: MessageSquare },
  // { to: '/admin/audit-log', label: 'Audit Log', icon: Shield }, // COMMENTED OUT
  { to: '/admin/website', label: 'Website', icon: Globe },
  { to: '/admin/terms', label: 'Terms & Conditions', icon: ScrollText },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
];

// ── Sidebar — defined OUTSIDE AdminLayout so it never remounts on re-render ──
function Sidebar({ mobile, sidebarOpen, user, settings, openQueryCount, pendingPoCount, pendingApprovalCount, openTicketCount, newEnquiryCount, onLogout }) {
  return (
    <div className={`flex flex-col h-full bg-gradient-to-b from-[#3d2b1f] to-[#4e3728] ${mobile ? 'w-72' : (sidebarOpen ? 'w-64' : 'w-16')} transition-all duration-300`}>
      {/* Logo */}
      <div className="flex items-center gap-3 p-4 border-b border-[#6b4c38]/50">
        <img src="/Logo.png" alt="Logo" className="w-9 h-9 rounded-xl object-contain flex-shrink-0 bg-white p-0.5" />
        {(sidebarOpen || mobile) && (
          <div>
            <p className="text-white font-bold text-sm leading-tight">{settings.company_name?.split(' ').slice(0, -1).join(' ') || 'Workshop'}</p>
            <p className="text-amber-400 text-xs">{settings.company_name?.split(' ').slice(-1)[0] || 'Manager'}</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                isActive
                  ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                  : 'text-[#c9a882] hover:bg-[#6b4c3899] hover:text-[#f5e6d3]'
              }`
            }
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {(sidebarOpen || mobile) && <span className="text-sm font-medium flex-1">{label}</span>}
            {(sidebarOpen || mobile) && label === 'Queries' && openQueryCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none">
                {openQueryCount > 99 ? '99+' : openQueryCount}
              </span>
            )}
            {(sidebarOpen || mobile) && label === 'Client POs' && pendingPoCount > 0 && (
              <span className="bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none">
                {pendingPoCount > 99 ? '99+' : pendingPoCount}
              </span>
            )}
            {(sidebarOpen || mobile) && label === 'Approvals' && pendingApprovalCount > 0 && (
              <span className="bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none">
                {pendingApprovalCount > 99 ? '99+' : pendingApprovalCount}
              </span>
            )}
            {(sidebarOpen || mobile) && label === 'Client Support' && openTicketCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none">
                {openTicketCount > 99 ? '99+' : openTicketCount}
              </span>
            )}
            {(sidebarOpen || mobile) && label === 'Website' && newEnquiryCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none">
                {newEnquiryCount > 99 ? '99+' : newEnquiryCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="p-3 border-t border-[#6b4c38]/50">
        <div className={`flex items-center gap-3 px-3 py-2 rounded-xl bg-[#6b4c3866] ${!(sidebarOpen || mobile) && 'justify-center'}`}>
          <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-sm font-bold">
            {user?.name?.charAt(0)}
          </div>
          {(sidebarOpen || mobile) && (
            <div className="flex-1 min-w-0">
              <p className="text-[#f5e6d3] text-xs font-semibold truncate">{user?.name}</p>
              <p className="text-[#c9a882] text-xs">Administrator</p>
            </div>
          )}
        </div>
        <button
          onClick={onLogout}
          className={`mt-2 flex items-center gap-3 px-3 py-2 rounded-xl text-[#c9a882] hover:text-red-300 hover:bg-[#6b4c3899] transition-all w-full ${!(sidebarOpen || mobile) && 'justify-center'}`}
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {(sidebarOpen || mobile) && <span className="text-sm">Logout</span>}
        </button>
      </div>
    </div>
  );
}

// ── Main Layout ───────────────────────────────────────────────────────────────
export default function AdminLayout() {
  const { secondsLeft, isIdle } = useInactivityLogout();
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openQueryCount, setOpenQueryCount] = useState(0);
  const [pendingPoCount, setPendingPoCount] = useState(0);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [newEnquiryCount, setNewEnquiryCount] = useState(0);

  useEffect(() => {
    const fetchQueryCount = () => {
      api.get('/queries', { params: { status: 'open' } })
        .then(r => {
          const pending = r.data.filter(q => q.status === 'open' || q.status === 'in_review');
          setOpenQueryCount(pending.length);
        })
        .catch(() => {});
    };
    const fetchPoCount = () => {
      api.get('/admin/client-purchase-orders')
        .then(r => setPendingPoCount(r.data.filter(p => p.status === 'pending').length))
        .catch(() => {});
    };
    const fetchApprovalCount = () => {
      api.get('/approvals/count')
        .then(r => setPendingApprovalCount(r.data?.total || 0))
        .catch(() => {});
    };
    const fetchTicketCount = () => {
      api.get('/admin/tickets')
        .then(r => setOpenTicketCount((r.data || []).filter(t => t.status !== 'closed').length))
        .catch(() => {}); // silently ignore (e.g. user lacks client_support permission)
    };
    const fetchEnquiryCount = () => {
      api.get('/website/admin/enquiries')
        .then(r => setNewEnquiryCount((r.data || []).filter(e => e.status === 'new').length))
        .catch(() => {});
    };
    fetchQueryCount();
    fetchPoCount();
    fetchApprovalCount();
    fetchTicketCount();
    fetchEnquiryCount();
    const interval = setInterval(() => { fetchQueryCount(); fetchPoCount(); fetchApprovalCount(); fetchTicketCount(); fetchEnquiryCount(); }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex flex-shrink-0">
        <Sidebar
          sidebarOpen={sidebarOpen}
          user={user}
          settings={settings}
          openQueryCount={openQueryCount}
          pendingPoCount={pendingPoCount}
          pendingApprovalCount={pendingApprovalCount}
          openTicketCount={openTicketCount}
              newEnquiryCount={newEnquiryCount}
          onLogout={handleLogout}
        />
      </div>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">
            <Sidebar
              mobile
              sidebarOpen={sidebarOpen}
              user={user}
              settings={settings}
              openQueryCount={openQueryCount}
              pendingPoCount={pendingPoCount}
              pendingApprovalCount={pendingApprovalCount}
              openTicketCount={openTicketCount}
              newEnquiryCount={newEnquiryCount}
              onLogout={handleLogout}
            />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button onClick={() => { setSidebarOpen(!sidebarOpen); setMobileOpen(!mobileOpen); }} className="p-2 rounded-lg hover:bg-gray-100">
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            {isIdle && (
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                secondsLeft <= 60
                  ? 'bg-red-50 text-red-600 border-red-200 animate-pulse'
                  : secondsLeft <= 300
                  ? 'bg-orange-50 text-orange-600 border-orange-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                <span>⏱</span>
                <span>
                  {secondsLeft <= 60
                    ? `Logout in ${secondsLeft}s`
                    : `Session: ${Math.floor(secondsLeft / 60)}m ${secondsLeft % 60}s`}
                </span>
              </div>
            )}
            <NotificationBell />
            <span className="text-xs text-gray-500 bg-amber-50 text-amber-700 px-3 py-1 rounded-full font-medium border border-amber-200">Admin Panel</span>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
          <FestivalAmbient />
        </main>
      </div>
    </div>
  );
}
